import React, { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Save, Plus, Edit2, Trash2, GripVertical } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ChapterAssemblyPage() {
  const [searchParams] = useSearchParams();
  const chapterId = searchParams.get('chapter_id');
  
  const [chapter, setChapter] = useState(null);
  const [paragraphs, setParagraphs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingParagraph, setEditingParagraph] = useState(null);

  useEffect(() => {
    if (chapterId) {
      fetchChapter();
    } else {
      // Load from session storage if coming from generation page
      const savedChapter = sessionStorage.getItem('currentChapter');
      if (savedChapter) {
        const data = JSON.parse(savedChapter);
        setChapter(data.chapter);
        setParagraphs(data.paragraphs || []);
        setLoading(false);
      }
    }
  }, [chapterId]);

  const fetchChapter = async () => {
    try {
      const response = await axios.get(`${API}/admin/generation/chapters`, {
        withCredentials: true
      });
      
      const foundChapter = response.data.chapters.find(c => c.id === chapterId);
      if (foundChapter) {
        setChapter(foundChapter);
        
        // Fetch actual paragraph content
        const paraPromises = foundChapter.paragraph_ids.map(async (paraId, idx) => {
          try {
            const paraResponse = await axios.get(
              `${API}/admin/generation/paragraph/${paraId}`,
              { withCredentials: true }
            );
            return {
              ...paraResponse.data.paragraph,
              order: idx + 1
            };
          } catch (err) {
            console.error(`Error fetching paragraph ${paraId}:`, err);
            return {
              id: paraId,
              order: idx + 1,
              content: 'Error loading paragraph content'
            };
          }
        });
        
        const paras = await Promise.all(paraPromises);
        setParagraphs(paras.filter(p => p !== null));
      }
    } catch (error) {
      console.error('Error fetching chapter:', error);
      toast.error('Failed to load chapter');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveParagraph = (paragraphId, newContent) => {
    setParagraphs(prev =>
      prev.map(p => p.id === paragraphId ? { ...p, content: newContent } : p)
    );
    setEditingParagraph(null);
    toast.success('Paragraph updated');
  };

  const handleReorderParagraphs = (fromIndex, toIndex) => {
    const newParagraphs = [...paragraphs];
    const [removed] = newParagraphs.splice(fromIndex, 1);
    newParagraphs.splice(toIndex, 0, removed);
    
    // Update order numbers
    newParagraphs.forEach((p, idx) => {
      p.order = idx + 1;
    });
    
    setParagraphs(newParagraphs);
    toast.success('Paragraphs reordered');
  };

  const handleAddToPublication = () => {
    // Save chapter data for publication assembly
    sessionStorage.setItem('publicationChapters', JSON.stringify([{
      ...chapter,
      paragraphs
    }]));
    
    toast.success('Chapter added to publication!');
    window.location.href = '/admin/publication-assembly';
  };

  const handleSaveChanges = async () => {
    try {
      // Update chapter with new paragraph order
      await axios.put(
        `${API}/admin/generation/chapters/${chapterId}`,
        {
          paragraph_ids: paragraphs.map(p => p.id)
        },
        { withCredentials: true }
      );
      
      toast.success('Changes saved!');
    } catch (error) {
      console.error('Error saving changes:', error);
      toast.error('Failed to save changes');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-2xl text-[#6B4423]">Loading chapter...</div>
        </div>
      </Layout>
    );
  }

  if (!chapter && !paragraphs.length) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-3xl text-[#6B4423] mb-4">No chapter data found</h1>
            <p className="text-gray-600 mb-4">Please create a chapter from the Generation page first.</p>
            <button
              onClick={() => window.location.href = '/admin/generation'}
              className="retro-button"
            >
              <ArrowLeft className="w-4 h-4 inline mr-2" />
              Go to Generation Page
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-b from-[#F4E4C1] to-[#E8D5B7] py-16">
        <div className="max-w-4xl mx-auto px-4">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <button
              onClick={() => window.location.href = '/admin/generation'}
              className="retro-button flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Generation
            </button>
            
            <h1 className="text-4xl retro-title text-[#6B4423]">Chapter Assembly</h1>
            
            <div className="flex gap-2">
              {chapterId && (
                <button
                  onClick={handleSaveChanges}
                  className="retro-button flex items-center gap-2"
                >
                  <Save className="w-4 h-4" />
                  Save Changes
                </button>
              )}
              <button
                onClick={handleAddToPublication}
                className="retro-button flex items-center gap-2 bg-green-600 hover:bg-green-700"
              >
                <Plus className="w-4 h-4" />
                Add to Publication
              </button>
            </div>
          </div>

          {/* Chapter Title */}
          <div className="admin-card mb-6">
            <input
              type="text"
              value={chapter?.title || 'Chapter Title'}
              onChange={(e) => setChapter(prev => ({ ...prev, title: e.target.value }))}
              className="text-3xl font-bold text-[#6B4423] w-full bg-transparent border-none focus:outline-none"
              placeholder="Enter chapter title..."
            />
          </div>

          {/* Chapter Content */}
          <div className="admin-card">
            <h2 className="text-2xl font-bold text-white mb-6">
              Paragraphs ({paragraphs.length})
            </h2>
            
            <div className="space-y-4">
              {paragraphs.map((para, index) => (
                <div key={para.id} className="relative group">
                  {/* Drag Handle */}
                  <div className="absolute left-0 top-0 bottom-0 w-8 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-move">
                    <GripVertical className="w-5 h-5 text-gray-400" />
                  </div>
                  
                  <div className="p-4 bg-white border-2 border-[#8B6F47] rounded hover:border-[#6B4423] transition-colors ml-8">
                    <div className="flex items-start justify-between mb-2">
                      <span className="text-sm font-semibold text-gray-600">
                        Paragraph {index + 1}
                      </span>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setEditingParagraph(para.id)}
                          className="text-[#6B4423] hover:text-[#8B6F47]"
                          title="Edit"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    
                    {editingParagraph === para.id ? (
                      <div>
                        <textarea
                          value={para.content}
                          onChange={(e) =>
                            setParagraphs(prev =>
                              prev.map(p =>
                                p.id === para.id ? { ...p, content: e.target.value } : p
                              )
                            )
                          }
                          className="w-full p-3 border-2 border-[#8B6F47] rounded h-32 resize-y"
                        />
                        <div className="flex gap-2 mt-2">
                          <button
                            onClick={() => handleSaveParagraph(para.id, para.content)}
                            className="retro-button text-sm"
                          >
                            Save
                          </button>
                          <button
                            onClick={() => setEditingParagraph(null)}
                            className="retro-button text-sm bg-gray-500"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="prose max-w-none text-black">{para.content}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Tools Notice */}
            <div className="mt-8 p-4 bg-[#F4E4C1] rounded border-2 border-[#8B6F47]">
              <p className="text-[#6B4423]">
                <strong>Coming in Phase 6:</strong> Full rich text editor, track changes, comments, advanced formatting
              </p>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
